<template>
  <div class="min-h-screen bg-[#F8FAFC] py-8 px-4 sm:px-6 lg:px-8">
    <div class="max-w-6xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">IPOs in India</h1>
        <!-- Keep in sync with resources/views/ssr/pages/ipo-index.blade.php -->
        <p class="mt-1 text-gray-600 max-w-3xl">Every IPO we track, grouped by where it is in its lifecycle. Open each issue for its price band, lot size, timeline, subscription and offer documents. New to IPOs? Start with <router-link to="/learn/what-is-an-ipo" class="text-[#2563EB] underline">what an IPO is</router-link>.</p>
        <DataFreshness />
      </div>

      <!-- Filter Bar -->
      <div class="mb-6 space-y-4">
        <div class="flex flex-col sm:flex-row gap-4">
          <input
            v-model="search"
            type="text"
            placeholder="Search IPOs..."
            class="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
          />
          <select v-model="sort" class="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]">
            <option value="newest">Newest</option>
            <option value="gmp_desc">GMP High-Low</option>
            <option value="issue_size">Issue Size</option>
            <option value="subscription">Subscription</option>
          </select>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="s in statuses"
            :key="s.value"
            @click="status = s.value; page = 1"
            :class="[
              'px-4 py-1.5 rounded-full text-sm font-medium transition',
              status === s.value ? 'bg-[#2563EB] text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ s.label }}
            <span v-if="s.count !== null" class="ml-1 opacity-75">({{ s.count }})</span>
          </button>
        </div>
      </div>

      <!-- Loading Skeleton -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="i in 6" :key="i" class="bg-white rounded-xl p-5 border border-gray-100 animate-pulse">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 rounded-full bg-gray-200"></div>
            <div class="flex-1">
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div class="h-6 w-16 bg-gray-200 rounded-full"></div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div v-for="j in 6" :key="j" class="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>

      <!-- IPO Cards -->
      <div v-else-if="ipos.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="ipo in ipos" :key="ipo.id" class="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition">
          <!-- Card Header -->
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-white font-bold text-sm"
                :style="{ backgroundColor: avatarColor(ipo.name) }"
              >
                {{ (ipo.name || '?')[0].toUpperCase() }}
              </div>
              <div class="min-w-0">
                <h2 class="font-semibold text-gray-900 text-sm line-clamp-2">{{ ipo.company?.name || ipo.name }}</h2>
                <div class="flex gap-1.5 mt-1">
                  <span v-if="ipo.exchange" class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{{ ipo.exchange }}</span>
                </div>
              </div>
            </div>
            <span :class="statusBadgeClass(ipo.status)" class="text-xs px-2.5 py-1 rounded-full font-medium capitalize">
              {{ ipo.status || '-' }}
            </span>
          </div>

          <!-- Metrics Grid -->
          <div class="grid grid-cols-2 gap-3 mb-4">
            <div class="bg-gray-50 rounded-lg p-2.5">
              <p class="text-xs text-gray-500">Price Band</p>
              <p class="text-sm font-semibold text-gray-900">{{ formatPriceBand(ipo) }}</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-2.5">
              <p class="text-xs text-gray-500">Lot Size</p>
              <p class="text-sm font-semibold text-gray-900">{{ ipo.lot_size ? `${ipo.lot_size} shares` : tba(ipo) }}</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-2.5">
              <p class="text-xs text-gray-500">Issue Size</p>
              <p class="text-sm font-semibold text-gray-900">{{ ipo.issue_size && ipo.issue_size <= 50000 ? `₹${ipo.issue_size} Cr` : tba(ipo) }}</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-2.5">
              <p class="text-xs text-gray-500">GMP</p>
              <p class="text-sm font-semibold" :class="gmpClass(ipo)">{{ formatGmp(ipo) }}</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-2.5">
              <p class="text-xs text-gray-500">Open Date</p>
              <p class="text-sm font-semibold text-gray-900">{{ formatDate(ipo.open_date) }}</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-2.5">
              <p class="text-xs text-gray-500">Close Date</p>
              <p class="text-sm font-semibold text-gray-900">{{ formatDate(ipo.close_date) }}</p>
            </div>
          </div>

          <!-- Footer -->
          <router-link
            :to="`/ipos/${ipo.slug}`"
            class="block text-center text-sm font-medium text-[#2563EB] hover:text-blue-800 py-2 border-t border-gray-100"
          >
            View Details →
          </router-link>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-16">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h2 class="mt-4 text-lg font-medium text-gray-900">No IPOs found</h2>
        <p class="mt-1 text-gray-500">Try adjusting your search or filter criteria.</p>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && lastPage > 1" class="mt-8 flex items-center justify-center gap-1">
        <button
          @click="page--"
          :disabled="page === 1"
          class="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          ← Prev
        </button>
        <button
          v-for="p in visiblePages"
          :key="p"
          @click="page = p"
          :class="[
            'w-9 h-9 rounded-lg text-sm font-medium',
            p === page ? 'bg-[#2563EB] text-white' : 'hover:bg-gray-100 text-gray-700'
          ]"
        >
          {{ p }}
        </button>
        <button
          @click="page++"
          :disabled="page === lastPage"
          class="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          Next →
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import DataFreshness from '../components/DataFreshness.vue';
// "TBA" only makes sense before an issue closes; afterwards a gap is just missing data.
const tba = (ipo) => ['upcoming', 'open'].includes(ipo.status) ? 'TBA' : '—';
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';

const statusFilter = ref('');



const ipos = ref([]);
const loading = ref(true);
const page = ref(1);
const lastPage = ref(1);
const total = ref(0);
const search = ref('');
const status = ref('');
const sort = ref('newest');
const counts = ref({ open: 0, upcoming: 0, closed: 0, listed: 0 });

const statuses = computed(() => [
  { label: 'All', value: '', count: null },
  { label: 'Open', value: 'open', count: counts.value.open },
  { label: 'Upcoming', value: 'upcoming', count: counts.value.upcoming },
  { label: 'Closed', value: 'closed', count: counts.value.closed },
  { label: 'Listed', value: 'listed', count: counts.value.listed },
]);

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, page.value - 2);
  const end = Math.min(lastPage.value, page.value + 2);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

let debounceTimer = null;
watch(search, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { page.value = 1; fetchIpos(); }, 300);
});
watch([status, sort, page], () => fetchIpos());

async function fetchIpos() {
  loading.value = true;
  try {
    const { data } = await axios.get('/api/ipos', {
      params: {
        status: status.value || undefined,
        search: search.value || undefined,
        sort: sort.value,
        per_page: 20,
        page: page.value,
      },
    });
    ipos.value = data.data;
    lastPage.value = data.last_page || data.meta?.last_page || 1;
    total.value = data.total || data.meta?.total || 0;
    if (data.counts) counts.value = data.counts;
  } catch (e) {
    ipos.value = [];
  } finally {
    loading.value = false;
  }
}

function avatarColor(name) {
  const colors = ['#2563EB', '#7C3AED', '#DC2626', '#D97706', '#16A34A', '#0891B2', '#BE185D'];
  // Hash the full name, not just the first letter — otherwise every company
  // starting with the same letter gets an identical avatar (same letter AND color).
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % colors.length;
  }
  return colors[Math.abs(hash) % colors.length];
}

function statusBadgeClass(s) {
  const map = {
    open: 'bg-green-100 text-green-700',
    upcoming: 'bg-blue-100 text-blue-700',
    closed: 'bg-amber-100 text-amber-700',
    listed: 'bg-gray-100 text-gray-600',
  };
  return map[s] || 'bg-gray-100 text-gray-600';
}

function formatPriceBand(ipo) {
  const min = ipo.price_band_min;
  const max = ipo.price_band_max;
  if (min == null && max == null) return 'TBA';
  if (min == null || max == null) return `₹${min ?? max}`;
  return `₹${min}-${max}`;
}

function formatGmp(ipo) {
  const gmp = ipo.latest_gmp?.gmp;
  if (gmp == null) return '-';
  return `₹${gmp}`;
}

function gmpClass(ipo) {
  const gmp = ipo.latest_gmp?.gmp;
  if (gmp == null) return 'text-gray-900';
  return gmp >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]';
}

function formatDate(d) {
  if (!d) return 'TBA';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

onMounted(fetchIpos);
</script>
